import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperPlaneTilt, Robot, User, X, ChatCircleDots } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      text: "Hello, I'm here to listen. This is a safe space where you can share whatever is on your mind. How are you feeling today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: inputValue,
        session_id: sessionId
      }, { withCredentials: true });

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: "I'm here for you. Sometimes I need a moment to gather my thoughts. Please try again, or reach out directly to Adeseun for immediate support.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        data-testid="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[9999] w-16 h-16 bg-[#B46C4A] hover:bg-[#9B5C3E] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <X size={28} weight="bold" /> : <ChatCircleDots size={28} weight="fill" />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 z-[9999] w-[380px] h-[500px] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-[#E5DCD0]"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-[#B46C4A] text-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Robot size={24} weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold font-['Outfit']">Safe Space AI</h3>
                <p className="text-sm text-white/80">Here to listen</p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-[#F2EBE1] px-4 py-2 text-xs text-[#5C564F]">
              <span className="font-medium">Peer Support Notice:</span> This is not a replacement for professional mental health care.
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start gap-2 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === 'user' ? 'bg-[#647B64]' : 'bg-[#F2EBE1]'
                    }`}>
                      {message.type === 'user' ? (
                        <User size={16} weight="fill" className="text-white" />
                      ) : (
                        <Robot size={16} weight="fill" className="text-[#B46C4A]" />
                      )}
                    </div>
                    <div className={message.type === 'user' ? 'chat-bubble-user px-4 py-3' : 'chat-bubble-ai px-4 py-3'}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-start gap-2"
                >
                  <div className="w-8 h-8 rounded-full bg-[#F2EBE1] flex items-center justify-center">
                    <Robot size={16} weight="fill" className="text-[#B46C4A]" />
                  </div>
                  <div className="chat-bubble-ai px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#B46C4A] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-[#B46C4A] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-[#B46C4A] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-[#E5DCD0]">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Share what's on your mind..."
                  className="flex-1 bg-[#F9F6F0] border border-[#E5DCD0] rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B46C4A]/20 focus:border-[#B46C4A]"
                  data-testid="chat-input"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="w-12 h-12 bg-[#B46C4A] hover:bg-[#9B5C3E] disabled:bg-[#E5DCD0] disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-all duration-300"
                  data-testid="chat-send-btn"
                >
                  <PaperPlaneTilt size={20} weight="fill" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
